import React, { useState, useMemo } from 'react';
import { Provider } from '../types';
import {
  X,
  UserPlus,
  UserCheck,
  ShieldCheck,
  AlertCircle,
  Stethoscope,
  Mail,
  Phone,
  CreditCard,
  FileCheck,
  DollarSign,
  Info
} from 'lucide-react';

interface AddTherapistModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTherapistAdded: (newProvider: Provider) => void;
  existingProviders: Provider[];
}

export const AddTherapistModal: React.FC<AddTherapistModalProps> = ({
  isOpen,
  onClose,
  onTherapistAdded,
  existingProviders
}) => {
  // Name & Credentials
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [credentials, setCredentials] = useState('MS, CCC-SLP');

  // Role & Title
  const [role, setRole] = useState<'SLP' | 'SLPA' | 'SUPERVISOR'>('SLP');
  const [title, setTitle] = useState('Speech-Language Pathologist');

  // Supervision (Required for SLPA, or for an SLP with requires_supervision)
  const [supervisorId, setSupervisorId] = useState('');
  const [requiresSupervision, setRequiresSupervision] = useState(false);

  // Contact Information
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Identifiers
  const [npi, setNpi] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [ahcccsId, setAhcccsId] = useState('');

  // Compensation / Payroll (Existing schema fields)
  const [ratePerSession, setRatePerSession] = useState<number | ''>('');
  const [ratePerEval, setRatePerEval] = useState<number | ''>('');
  const [payLadder, setPayLadder] = useState<'STANDARD' | 'CF_PLUS_5'>('STANDARD');
  const [payType, setPayType] = useState<'PER_SESSION' | 'HOURLY' | 'SALARY'>('PER_SESSION');

  // Status
  const [active, setActive] = useState(true);

  // Submission State
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Available supervisors (Active SLPs or Supervisors)
  const availableSupervisors = useMemo(() => {
    return existingProviders.filter(
      (p) => (p.role === 'SLP' || p.role === 'SUPERVISOR')
        && (p.active === undefined || p.active === 1)
        && !p.requires_supervision
    );
  }, [existingProviders]);

  // Handle role change and auto-set appropriate default credentials & title
  const handleRoleChange = (newRole: 'SLP' | 'SLPA' | 'SUPERVISOR') => {
    setRole(newRole);
    if (newRole === 'SLPA') {
      setTitle('Speech-Language Pathology Assistant');
      if (credentials === 'MS, CCC-SLP' || credentials === '') {
        setCredentials('SLPA');
      }
      // Auto-suggest first available supervisor if not selected
      if (!supervisorId && availableSupervisors.length > 0) {
        setSupervisorId(availableSupervisors[0].id);
      }
      setRequiresSupervision(false);
    } else if (newRole === 'SUPERVISOR') {
      setTitle('Clinical Supervisor / Lead SLP');
      if (credentials === 'SLPA' || credentials === '') {
        setCredentials('MS, CCC-SLP');
      }
      setSupervisorId('');
      setRequiresSupervision(false);
    } else {
      setTitle('Speech-Language Pathologist');
      if (credentials === 'SLPA' || credentials === '') {
        setCredentials('MS, CCC-SLP');
      }
      if (!requiresSupervision) setSupervisorId('');
    }
  };

  // Live computed display name
  const formattedDisplayName = useMemo(() => {
    const fn = firstName.trim();
    const ln = lastName.trim();
    const creds = credentials.trim();
    if (!fn && !ln) return '';
    const base = `${fn} ${ln}`.trim();
    return creds ? `${base}, ${creds}` : base;
  }, [firstName, lastName, credentials]);

  // Client-side duplicate detection warning
  const duplicateWarning = useMemo(() => {
    if (!existingProviders || existingProviders.length === 0) return null;

    // Check NPI duplicate
    const cleanNpi = npi.trim().replace(/[^0-9]/g, '');
    if (cleanNpi && cleanNpi.length >= 10) {
      const match = existingProviders.find(
        (p) => p.npi && p.npi.replace(/[^0-9]/g, '') === cleanNpi
      );
      if (match) {
        return `A provider with NPI ${cleanNpi} already exists in the system: ${match.name} (${match.id})`;
      }
    }

    // Check License duplicate
    const cleanLic = licenseNumber.trim().toLowerCase();
    if (cleanLic && cleanLic.length >= 3) {
      const match = existingProviders.find(
        (p) => p.license_number && p.license_number.trim().toLowerCase() === cleanLic
      );
      if (match) {
        return `A provider with License Number "${licenseNumber.trim()}" already exists: ${match.name} (${match.id})`;
      }
    }

    // Check Email duplicate
    const cleanEmail = email.trim().toLowerCase();
    if (cleanEmail && cleanEmail.includes('@')) {
      const match = existingProviders.find(
        (p) => p.email && p.email.trim().toLowerCase() === cleanEmail
      );
      if (match) {
        return `A provider with email "${email.trim()}" already exists: ${match.name} (${match.id})`;
      }
    }

    return null;
  }, [existingProviders, npi, licenseNumber, email]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      setError('First name and last name are required.');
      return;
    }

    if ((role === 'SLPA' || requiresSupervision) && !supervisorId) {
      setError(role === 'SLPA'
        ? 'A designated Supervising SLP is required when registering a Speech-Language Pathology Assistant (SLPA).'
        : 'A designated Supervising SLP is required when this SLP requires supervision.');
      return;
    }

    if (duplicateWarning) {
      setError(`Cannot save: ${duplicateWarning}`);
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: formattedDisplayName,
        first_name: fn,
        middle_name: middleName.trim() || null,
        last_name: ln,
        suffix: suffix.trim() || null,
        credentials: credentials.trim() || null,
        role,
        title: title.trim() || (role === 'SLPA' ? 'Speech-Language Pathology Assistant' : 'Speech-Language Pathologist'),
        email: email.trim() || null,
        phone: phone.trim() || null,
        npi: npi.trim() ? npi.trim().replace(/[^0-9]/g, '') : null,
        license_number: licenseNumber.trim() || null,
        ahcccs_id: ahcccsId.trim() || null,
        supervisor_id: (role === 'SLPA' || requiresSupervision) && supervisorId ? supervisorId : null,
        rate_per_session: ratePerSession !== '' ? Number(ratePerSession) : null,
        rate_per_eval: ratePerEval !== '' ? Number(ratePerEval) : null,
        pay_ladder: payLadder,
        requires_supervision: role === 'SLP' && requiresSupervision ? 1 : 0,
        pay_type: payType,
        active: active ? 1 : 0
      };

      const res = await fetch('/api/providers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const errorMsg = data.error || (res.status === 403 ? 'You do not have permission to register providers.' : 'Failed to create therapist record.');
        throw new Error(errorMsg);
      }

      // Reset form
      setFirstName('');
      setMiddleName('');
      setLastName('');
      setSuffix('');
      setCredentials('MS, CCC-SLP');
      setNpi('');
      setLicenseNumber('');
      setAhcccsId('');
      setEmail('');
      setPhone('');

      // Notify parent
      if (data.provider) {
        onTherapistAdded(data.provider);
      } else {
        // Fallback reconstructed provider
        onTherapistAdded({
          id: data.provId,
          name: payload.name,
          role: payload.role as any,
          title: payload.title,
          email: payload.email || undefined,
          phone: payload.phone || undefined,
          npi: payload.npi || undefined,
          license_number: payload.license_number || undefined,
          supervisor_id: payload.supervisor_id || undefined,
          rate_per_session: payload.rate_per_session || undefined,
          rate_per_eval: payload.rate_per_eval || undefined,
          pay_ladder: payload.pay_ladder,
          pay_type: payload.pay_type,
          active: payload.active
        });
      }

      onClose();
    } catch (err: any) {
      console.error('[AddTherapistModal] Creation error:', err);
      setError(`Unable to create therapist: ${err.message || 'An unexpected error occurred while saving provider.'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="glass-panel max-w-xl w-full rounded-2xl border border-slate-800 p-6 space-y-4 my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
              <UserPlus className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center space-x-2">
                <span>Add Therapist / Clinical Provider</span>
              </h2>
              <p className="text-[11px] text-slate-400">
                Register a new clinician for caseload assignments, scheduling, documentation, and billing
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Identity & RBAC Separation Clarification Banner */}
        <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 text-[11px] text-slate-300 space-y-1">
          <div className="flex items-center space-x-1.5 text-sky-400 font-semibold">
            <Info className="w-3.5 h-3.5" />
            <span>Clinical Provider Profile First (App Login Independent)</span>
          </div>
          <p className="text-slate-400 leading-relaxed">
            This creates an official <strong className="text-slate-200">Clinical Provider</strong> record in the EMR for client assignments, visit planners, SOAP authoring, and billing. It <span className="text-amber-300">does not require or create a Bloom login account</span>. App login access is optional and completely independent.
          </p>
        </div>

        {/* Error / Duplicate Warning Alert */}
        {(error || duplicateWarning) && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg text-xs flex items-start space-x-2.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-rose-400" />
            <div className="space-y-0.5">
              <p className="font-semibold">{error ? 'Validation Error' : 'Duplicate Identifier Detected'}</p>
              <p className="text-[11px] text-rose-200/90">{error || duplicateWarning}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Section 1: Name & Credentials */}
          <div className="space-y-2">
            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center space-x-1.5">
              <UserCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>Clinician Name & Credentials</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  First Name <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="e.g. Maria"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Middle Name / Initial
                </label>
                <input
                  type="text"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  placeholder="Optional"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Last Name <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="e.g. Gonzales"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Suffix
                </label>
                <input
                  type="text"
                  value={suffix}
                  onChange={(e) => setSuffix(e.target.value)}
                  placeholder="Optional, e.g. Jr"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Credentials
                </label>
                <input
                  type="text"
                  value={credentials}
                  onChange={(e) => setCredentials(e.target.value)}
                  placeholder="e.g. MS, CCC-SLP"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Live Name Preview */}
            {formattedDisplayName && (
              <div className="px-3 py-1.5 bg-slate-950/70 border border-slate-800/80 rounded-lg flex items-center justify-between text-[11px]">
                <span className="text-slate-400">Clinical Display Name:</span>
                <span className="font-semibold text-emerald-300 font-mono">
                  {formattedDisplayName}
                </span>
              </div>
            )}
          </div>

          {/* Section 2: Clinical Role & Supervision */}
          <div className="space-y-2 pt-2 border-t border-slate-800/80">
            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center space-x-1.5">
              <Stethoscope className="w-3.5 h-3.5 text-sky-400" />
              <span>Provider Type & Clinical Role</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Provider Type <span className="text-rose-400">*</span>
                </label>
                <select
                  value={role}
                  onChange={(e) => handleRoleChange(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-sky-500 font-medium"
                >
                  <option value="SLP">SLP - Speech-Language Pathologist</option>
                  <option value="SLPA">SLPA - Speech-Language Pathology Assistant</option>
                  <option value="SUPERVISOR">SUPERVISOR - Clinical Supervisor / Lead SLP</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Position / Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Speech-Language Pathologist"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
                />
              </div>
            </div>

            {role === 'SLP' && (
              <label className="flex items-start space-x-2 p-3 rounded-xl border border-slate-700 bg-slate-900/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requiresSupervision}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setRequiresSupervision(next);
                    if (!next) setSupervisorId('');
                    else if (!supervisorId && availableSupervisors.length > 0) {
                      setSupervisorId(availableSupervisors[0].id);
                    }
                  }}
                  className="mt-0.5"
                />
                <span className="text-[11px] text-slate-300 leading-snug">
                  <strong className="text-slate-100">Requires clinical supervision.</strong> Use for a CF SLP. Role and pay stay SLP. SOAP/QPR notes use the existing supervisor approval workflow.
                </span>
              </label>
            )}

            {/* Supervising SLP (Required for SLPA or supervised SLP) */}
            {(role === 'SLPA' || (role === 'SLP' && requiresSupervision)) && (
              <div className="p-3 bg-indigo-950/20 border border-indigo-800/40 rounded-xl space-y-1.5">
                <label className="block text-indigo-300 font-semibold flex items-center space-x-1.5">
                  <ShieldCheck className="w-4 h-4 text-indigo-400" />
                  <span>Designated Supervising SLP {role === 'SLPA' ? '(Required for Assistants)' : '(Required for supervised SLP)'} <span className="text-rose-400">*</span></span>
                </label>
                <select
                  required
                  value={supervisorId}
                  onChange={(e) => setSupervisorId(e.target.value)}
                  className="w-full bg-slate-900 border border-indigo-700/60 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-indigo-400"
                >
                  <option value="">-- Select Supervising SLP --</option>
                  {availableSupervisors.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.role}) {s.npi ? `• NPI: ${s.npi}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-indigo-200/80">
                  {role === 'SLPA'
                    ? 'All clinical notes and plans of care authored by this SLPA will automatically route to the selected supervising SLP for review and co-signature.'
                    : 'SOAP and QPR notes authored by this SLP will route to the selected supervising SLP for review and co-signature. Evaluations and POCs stay SLP-authored.'}
                </p>
              </div>
            )}
          </div>

          {/* Section 3: Professional Identifiers & Contact */}
          <div className="space-y-2 pt-2 border-t border-slate-800/80">
            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center space-x-1.5">
              <FileCheck className="w-3.5 h-3.5 text-amber-400" />
              <span>Licensure, NPI & Contact Info</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="block text-slate-300 font-semibold mb-1 flex items-center space-x-1">
                  <CreditCard className="w-3 h-3 text-slate-400" />
                  <span>NPI Number (10 Digits)</span>
                </label>
                <input
                  type="text"
                  value={npi}
                  onChange={(e) => setNpi(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                  placeholder="e.g. 1892837461"
                  maxLength={10}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 font-mono placeholder-slate-500 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1 flex items-center space-x-1">
                  <FileCheck className="w-3 h-3 text-slate-400" />
                  <span>State Clinical License #</span>
                </label>
                <input
                  type="text"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  placeholder="e.g. SLP-AZ-382910"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 font-mono placeholder-slate-500 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1 flex items-center space-x-1">
                  <CreditCard className="w-3 h-3 text-slate-400" />
                  <span>Individual AHCCCS ID</span>
                </label>
                <input
                  type="text"
                  value={ahcccsId}
                  onChange={(e) => setAhcccsId(e.target.value)}
                  placeholder="e.g. 123456"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 font-mono placeholder-slate-500 focus:outline-none focus:border-amber-500"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Required for rendering provider billing on DDD / AHCCCS claims.</p>
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1 flex items-center space-x-1">
                  <Mail className="w-3 h-3 text-slate-400" />
                  <span>Clinical Contact / eSignature Email</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. clinician@allegianttherapy.com or personal email"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Used for clinical communications and SignWell routing. Does not require an @allegianttherapy.com account or Bloom login.
                </p>
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1 flex items-center space-x-1">
                  <Phone className="w-3 h-3 text-slate-400" />
                  <span>Phone Number</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(480) 555-0199"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Payroll Rates & Status (Existing Schema Fields) */}
          <div className="space-y-2 pt-2 border-t border-slate-800/80">
            <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center space-x-1.5">
              <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
              <span>Payroll Compensation & Active Status</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Treatment Pay Ladder
                </label>
                <select
                  value={payLadder}
                  onChange={(e) => setPayLadder(e.target.value === 'CF_PLUS_5' ? 'CF_PLUS_5' : 'STANDARD')}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-emerald-500 text-xs"
                >
                  <option value="STANDARD">STANDARD — existing weekly ladder</option>
                  <option value="CF_PLUS_5">CF_PLUS_5 — same bands, +$5 each tier</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Rate Per Treatment ($/session)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={ratePerSession}
                  onChange={(e) => setRatePerSession(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Leave empty for Ladder ($25–$85)"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 font-mono focus:outline-none focus:border-emerald-500 text-xs"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Rate Per Eval ($/eval)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={ratePerEval}
                  onChange={(e) => setRatePerEval(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="Leave empty for Standard ($150/$175)"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 font-mono focus:outline-none focus:border-emerald-500 text-xs"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-semibold mb-1">
                  Pay Model
                </label>
                <select
                  value={payType}
                  onChange={(e) => setPayType(e.target.value as any)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 focus:outline-none focus:border-emerald-500"
                >
                  <option value="PER_SESSION">Per Session / Visit</option>
                  <option value="HOURLY">Hourly</option>
                  <option value="SALARY">Salary</option>
                </select>
              </div>
            </div>

            <div className="pt-2 flex items-center justify-between">
              <label className="flex items-center space-x-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-0 w-4 h-4 cursor-pointer"
                />
                <span className="text-slate-300 font-medium">
                  Active Provider (Available for immediate client assignments & scheduling)
                </span>
              </label>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-3 flex items-center justify-end space-x-2.5 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !!duplicateWarning}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-xs font-bold transition-colors shadow-sm flex items-center space-x-1.5"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>{saving ? 'Creating Provider...' : 'Save & Add Therapist'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
